var AnimationSupport = require("enyo-anim/AnimationSupport"),
    Animator         = require("enyo-anim/Animator"),
    Attractor        = require("enyo-anim/Attractor"),
    CoreObject       = require("enyo/CoreObject"),
    PerformanceMode  = require("enyo-perf/PerformanceMode"),
    Scalar           = require("enyo-anim/Scalar"),
    kind             = require("enyo/kind"),
    logger           = require("enyo/logger");

require("enyo-ui");

/**
 * An abstract class defining some required methods and common functionality to
 * the transition of a slide in a [Slider]{@link enyo-ui.Slider} control between
 * active and inactive state.
 *
 * This class is not meant to be used directly, but rather to be extended to
 * implement a particular transition for slide controls.
 *
 * @ui
 * @class   enyo-ui.SliderTransition
 * @extends enyo.CoreObject
 * @mixes   enyo-anim.AnimationSupport
 * @abstract
 * @public
 */
module.exports = kind(/** @lends  enyo-ui.SliderTransition.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.SliderTransition",

    /**
     * @private
     */
    kind: CoreObject,

    /**
     * @private
     */
    mixins: [AnimationSupport],

    /**
     * The slide.
     *
     * @type {enyo.Control}
     * @private
     */
    slide: null,

    /**
     * The animator.
     *
     * @type {enyo-anim.Animator}
     * @private
     */
    animation: null,

    /**
     * Whether the transition is animating.
     *
     * @type {boolean}
     * @readonly
     * @public
     */
    animating: false,

    /**
     * This observable value is used to publish the value of the transition
     * scalar.
     *
     * @type {?number}
     * @readonly
     * @public
     */
    value: null,

    /**
     * Setup animation
     *
     * @param  {Object}          props             Optional instance properties
     * @param  {enyo-ui.Slider}  props.slider      The slider
     * @param  {enyo.Control}    props.slide       The slide
     * @param  {number=}         props.epsilon     The epsilon value for the
     *                                             transition scalar
     * @param  {number=}         props.friction    The epsilon value for the
     *                                             transition scalar
     * @param  {number=}         props.attraction  The epsilon value for the
     *                                             transition attractor
     * @private
     */
    constructor: function(props) {
        CoreObject.prototype.importProps.apply(this, arguments);
        props          = props || {};
        this.animation = new Animator({
            scalar: new Scalar({
                value:    0,
                epsilon:  props.epsilon || 0.0001,
                friction: props.friction || Scalar.prototype.friction
            }),
            attractors: {
                target: new Attractor({
                    attraction: props.attraction || Attractor.prototype.attraction
                })
            }
        });
        this.set("value", this.animation.scalar.value);
        this.update    = this.bindSafely(this.update);
    },

    /**
     * @method
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.stop();
            sup.apply(this, arguments);
        };
    }),

    // Snap
    // --------------------------------

    /**
     * Snap to active position.
     *
     * @public
     */
    snapToActive: function() {
        this.animation.attractors.target.value = 0;
        this.snap();
    },

    /**
     * Snap to the inactive position.
     *
     * @param  {string=}  direction  The direction of this transition
     * @public
     */
    snapToInactive: function(direction) {
        var progress = this.animation.scalar,
            target   = this.animation.attractors.target;

        switch (direction) {
            case "forward":
                target.value = 1;
                break;
            case "backward":
                target.value = -1;
                break;
            default:
                target.value = progress.value >= 0 ? 1 : -1;
                break;
        }

        this.snap();
    },

    // Transition
    // --------------------------------

    /**
     * Start a transition from wherever the slide is now, to active position.
     *
     * @param  {string=}  direction  The direction of this transition
     * @public
     */
    transitionToActive: function(direction) {
        var progress = this.animation.scalar,
            target   = this.animation.attractors.target;

        // Sets a proper starting position
        if (Math.abs(progress.value) >= 1) {
            progress.value = direction === "backward" ? -1 : 1;
        }

        target.value    = 0;
        !this.animating && this.start();
    },

    /**
     * Start a transition from wherever the slide is now, to the nearest
     * inactive position.
     *
     * @param  {string=}  direction  The direction of this transition
     * @public
     */
    transitionToInactive: function(direction) {
        var progress = this.animation.scalar,
            target   = this.animation.attractors.target;

        switch (direction) {
            case "backward":
                target.value = progress.value <= 0 ? 1 : -1;
                break;
            case "forward":
            default:
                target.value = progress.value >= 0 ? -1 : 1;
                break;
        }

        !this.animating && this.start();
    },

    // Animation Loop
    // --------------------------------

    /**
     * Start the transition.
     *
     * @private
     */
    start: function() {
        if (this.animating) {
            return;
        }

        // Start animation
        PerformanceMode.enable();
        this.set("animating", true);
        this.update();
    },

    /**
     * Execute a frame in the transition animation.
     *
     * @private
     */
    update: function() {
        var animation = this.animation,
            finished  = false;

        if (!this.animating) {
            return;
        }

        animation.update();

        if (animation.isDone()) {
            animation.finish();
            finished = true;
        }

        this.apply();
        this.set("value", animation.scalar.value);

        if (!finished) {
            this.requestAnimationFrame("update", this.update);
        }
        else {
            this.stop();
        }
    },

    /**
     * Apply the current transition state.
     *
     * @public
     */
    apply: function() {
        logger.error("SlideTransition must implement a `apply` method");
    },

    /**
     * Snap the transition to it's final position and stop the animation.
     *
     * @public
     */
    snap: function() {
        var animation  = this.animation;

        // We leave the target in-tact, so we can still infer whether the slide
        // had been transitioning into active position or not.
        animation.finish();
        this.apply();
        this.set("value", animation.scalar.value);
        this.animating && this.stop();
    },

    /**
     * Stop the transition
     *
     * @public
     */
    stop: function() {
        if (!this.animating) {
            return;
        }

        this.animation.stop();
        this.cancelAnimationFrame("update");
        PerformanceMode.disable();
        this.set("animating", false);
    }

});
